import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { EditorActions } from '../editor/editorSlice';
import { PostType } from '../graphql/generated/graphql';
import PostArchivedBar from './PostArchivedBar';
import PostDraftBar from './PostDraftBar';
import styles from './PostStateBar.module.scss';
import PostTemplateBar from './PostTemplateBar';
import PostVerificationExpiredBar from './PostVerificationExpiredBar';
import PostVerificationRequestedBar from './PostVerificationRequestedBar';
import PostVerifiedBar from './PostVerifiedBar';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import { isVerifier } from './postPolicyCheck';
import { usePostTOC } from './PostTOCContext';
import addTestId from '../utils/addTestId';
import { gql } from '../graphql/generated/gql';
import { isFeatureEnabled } from '../utils/features';

const BarComponents = {
  draft: PostDraftBar,
  archived: PostArchivedBar,
  verified: PostVerifiedBar,
  verificationRequested: PostVerificationRequestedBar,
  verificationExpired: PostVerificationExpiredBar,
  template: PostTemplateBar,
};

const POST_QUERY = gql(`
  query PostStateBar($id: ID!) {
    post(id: $id) {
      id
      archivedAt
      publishedAt
      type
      ...isVerifier_post
      ...getVerificationStatus_post
    }
  }
`);

interface PostStateBarProps {
  gridItem: HTMLElement;
  hide?: boolean;
  postId: string;
  tocContainerRef?: React.RefObject<HTMLElement>;
  magneticWallContainerRef?: React.RefObject<HTMLElement>;
}

const PostStateBar = ({
  gridItem,
  hide = false,
  postId,
  magneticWallContainerRef,
  tocContainerRef,
}: PostStateBarProps) => {
  const { isTOCOpen } = usePostTOC();
  const dispatch = useAppDispatch();
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const tocContainerEl = tocContainerRef?.current;
    const magneticWallContainerEl = magneticWallContainerRef?.current;
    if (
      !containerEl ||
      !isFeatureEnabled('new_post_layout') ||
      (!tocContainerEl && !magneticWallContainerEl)
    ) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      containerEl.style.marginLeft = `${gridItem.offsetLeft}px`;
    });

    if (tocContainerEl) {
      observer.observe(tocContainerEl);
    }
    if (magneticWallContainerEl) {
      observer.observe(magneticWallContainerEl);
    }

    return () => {
      observer.disconnect();
    };
  }, [gridItem, tocContainerRef, magneticWallContainerRef, containerEl]);

  useEffect(() => {
    const gridContainer = gridItem.parentElement;

    if (!gridContainer) return undefined;

    if (containerEl) {
      containerEl.style.marginLeft = `${gridItem.offsetLeft}px`;
    }
    setWidth(gridItem.clientWidth);

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === gridItem) {
          setWidth(entry.contentRect.width);
        }
      });

      if (containerEl) {
        containerEl.style.marginLeft = `${gridItem.offsetLeft}px`;
      }
    });
    observer.observe(gridItem);
    observer.observe(gridContainer);

    return () => {
      observer.disconnect();
    };
  }, [isTOCOpen, gridItem, containerEl]);

  const { data } = useQuery(POST_QUERY, { variables: { id: postId } });
  const post = data?.post;

  const currentUser = useAppSelector((state) => state.session.user);
  let variant: keyof typeof BarComponents | null = null;

  if (post) {
    if (post.archivedAt) {
      variant = 'archived';
    } else if (!post.publishedAt) {
      variant = 'draft';
    } else {
      const verificationStatus = getVerificationStatus(post);
      if (verificationStatus === VerificationStatus.Verified) {
        variant = 'verified';
      } else if (verificationStatus === VerificationStatus.Expired) {
        variant = 'verificationExpired';
      } else if (
        verificationStatus === VerificationStatus.Requested &&
        isVerifier(post, currentUser)
      ) {
        variant = 'verificationRequested';
      } else if (post.type === PostType.Template) {
        variant = 'template';
      }
    }
  }

  const hasStateBar = !!variant;
  useLayoutEffect(() => {
    dispatch(EditorActions.setHasStateBar(hasStateBar));
  }, [dispatch, hasStateBar]);

  const BarComponent = variant && BarComponents[variant];

  return post && variant && BarComponent ? (
    <div
      data-no-print
      {...addTestId('postStateBar')}
      className={classNames(styles.container, styles[variant], {
        [styles.hide]: hide,
      })}
    >
      <div ref={setContainerEl} style={{ width }}>
        <BarComponent postId={post.id} />
      </div>
    </div>
  ) : null;
};

export default PostStateBar;
